import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainButton } from '@telegram-apps/sdk-react';
import { TFunction } from 'i18next';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { SmokingStatus } from '@/enums/apiEnums.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import SmokingShape from '@/components/common/svg/shapes/SmokingShape.tsx';

export const getSmokingOptions = (
  t: TFunction
): Record<SmokingStatus, string> => ({
  [SmokingStatus.SMOKING_STATUS_UNSPECIFIED]: t('status_unspecified'),
  [SmokingStatus.SMOKING_STATUS_YES]: t('smoking_status.yes'),
  [SmokingStatus.SMOKING_STATUS_OCCASIONALLY]: t('smoking_status.occasionally'),
  [SmokingStatus.SMOKING_STATUS_NONSMOKER]: t('smoking_status.nonsmoker')
});

interface EditSmokingStatusPopupProps {
  handleClose: () => void;
}

const EditSmokingStatusPopup: FC<EditSmokingStatusPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);
  
  const { aboutMe, updateProfile } = useProfileStore();
  const { t } = useTranslation();

  const smokingOptions = getSmokingOptions(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<SmokingStatus>(
    aboutMe?.profile.smoking_status ?? SmokingStatus.SMOKING_STATUS_UNSPECIFIED
  );

  const selectSmokingStatus = (value: SmokingStatus) => {
    setIsEdited(true);
    setSelectedStatus(value);
  };

  const submitSmokingStatus = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ smoking_status: selectedStatus });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitSmokingStatus);
    return () => mainButton.offClick(submitSmokingStatus);
  }, [selectedStatus]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <SmokingShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_smoking')}</div>
      </div>
      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(smokingOptions).map(([value, label]) => (
                <div
                  key={value}
                  onClick={() => selectSmokingStatus(value as SmokingStatus)}
                  style={{
                    ...listItemStyle,
                    ...(selectedStatus === (value as SmokingStatus)
                      ? selectedItemStyle
                      : unselectedItemStyle),
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {label}
                  {selectedStatus === (value as SmokingStatus) && (
                    <span style={selectedItemMarkStyle}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditSmokingStatusPopup;
